import { delay } from 'es-toolkit/promise';
import { AnimatePresence, motion } from 'framer-motion';
import { CloudUpload, DollarSign, KeyRound, Lock } from 'lucide-react';
import { type ReactNode, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { usePreferences } from '../../contexts/PreferencesContext';
import { cn } from '../../lib/utils';
import { captureAndReportError } from '../../utils/errorReporting';
import { isUploadProviderEnabled } from '../../utils/providerHelpers';
import { hasS3Config } from '../../utils/s3CredentialManager';
import { Button } from '../Button';
import { EASE_OUT, StatusPill, type StatusPillTone } from '../portal';
import { SettingsPage, SettingsRailCard, SettingsSection } from '../settings';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../ui/tooltip';
import { providers } from './providers';
import type { ProviderType } from './providers/types';

function InfoPill({
  tone,
  icon,
  label,
  title,
  points,
}: {
  tone: StatusPillTone;
  icon: ReactNode;
  label: string;
  title: string;
  points: string[];
}) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className="cursor-help">
          <StatusPill tone={tone} icon={icon}>
            {label}
          </StatusPill>
        </span>
      </TooltipTrigger>
      <TooltipContent className="max-w-64 border-seeker-700 bg-seeker-800 p-3">
        <p className="font-medium text-white">{title}</p>
        <ul className="mt-1 list-inside list-disc space-y-0.5 text-seeker-400">
          {points.map((point) => (
            <li key={point}>{point}</li>
          ))}
        </ul>
      </TooltipContent>
    </Tooltip>
  );
}

export const PreferencesSettingsPage = () => {
  const { preferences, updatePreferences, isLoading, error } = usePreferences();
  const [selectedProvider, setSelectedProvider] = useState<ProviderType | null>(
    isUploadProviderEnabled(preferences.uploadProvider)
      ? preferences.uploadProvider
      : null,
  );
  const [hasChanges, setHasChanges] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [providerConfigs, setProviderConfigs] = useState({
    s3: false,
  });

  useEffect(() => {
    setSelectedProvider(
      isUploadProviderEnabled(preferences.uploadProvider)
        ? preferences.uploadProvider
        : null,
    );
  }, [preferences.uploadProvider]);

  useEffect(() => {
    setHasChanges(
      selectedProvider !== null &&
        selectedProvider !== preferences.uploadProvider,
    );
  }, [selectedProvider, preferences.uploadProvider]);

  useEffect(() => {
    if (error) {
      toast.error(error);
    }
  }, [error]);

  useEffect(() => {
    const checkConfigs = () => {
      setProviderConfigs({
        s3: hasS3Config(),
      });
    };

    checkConfigs();
    // Re-check on focus in case config was added in another tab
    const handleFocus = () => checkConfigs();
    window.addEventListener('focus', handleFocus);

    return () => window.removeEventListener('focus', handleFocus);
  }, []);

  const handleProviderChange = (provider: ProviderType) => {
    setSelectedProvider(provider);
  };

  const handleSave = async () => {
    if (!hasChanges || selectedProvider === null) return;

    if (selectedProvider === 's3' && !hasS3Config()) {
      toast.error('Please configure AWS S3 before saving');
      return;
    }

    const saveStartTime = Date.now();

    setIsSaving(true);
    try {
      await updatePreferences({ uploadProvider: selectedProvider });

      const elapsedTime = Date.now() - saveStartTime;
      const minimumSpinnerTime = 1000;
      const remainingTime = Math.max(0, minimumSpinnerTime - elapsedTime);

      if (remainingTime > 0) {
        await delay(remainingTime);
      }

      toast.success('Storage settings saved');
      setHasChanges(false);
    } catch (err) {
      captureAndReportError('Failed to save preferences:', err);
      toast.error('Failed to save preferences');
    } finally {
      setIsSaving(false);
    }
  };

  const saveDisabled =
    !hasChanges || isSaving || isLoading || selectedProvider === null;
  const selectedProviderInfo = providers.find(
    (provider) => provider.id === selectedProvider,
  );
  // "Needs setup" covers both "nothing picked yet" and "S3 without credentials";
  // every other provider uploads with no extra configuration.
  const needsSetup =
    selectedProviderInfo === undefined ||
    (selectedProvider === 's3' && !providerConfigs.s3);

  return (
    <TooltipProvider>
      <SettingsPage
        icon={<CloudUpload />}
        title="Storage"
        description="Where your app assets and NFT metadata are uploaded."
        actions={
          <Button
            variant="primary"
            onClick={handleSave}
            disabled={saveDisabled}
            submitting={isSaving}
            className="w-full sm:w-auto sm:min-w-[140px]"
            title={
              selectedProvider === null
                ? 'Please select a storage provider'
                : selectedProvider === 's3' && !providerConfigs.s3
                  ? 'Configure S3 before saving'
                  : undefined
            }
          >
            Save Changes
          </Button>
        }
        rail={
          <SettingsRailCard title="How uploads work">
            <ul>
              <li>Assets are uploaded when you save or publish a listing.</li>
              <li>NFT metadata is uploaded when you mint.</li>
              <li>Switching providers only affects future uploads.</li>
            </ul>
          </SettingsRailCard>
        }
      >
        <SettingsSection
          title="Upload Provider"
          description="Choose your preferred permanent storage provider for uploading NFT metadata and assets."
          action={
            isLoading ? null : (
              <>
                <span className="text-sm leading-5 text-white">
                  {selectedProviderInfo?.info.name ?? 'No provider selected'}
                </span>
                <StatusPill tone={needsSetup ? 'required' : 'published'}>
                  {needsSetup ? 'Needs setup' : 'Ready'}
                </StatusPill>
              </>
            )
          }
        >
          <div
            role="radiogroup"
            aria-label="Upload provider"
            className="flex flex-col gap-4"
          >
            {providers.map((provider) => {
              const isSelected = selectedProvider === provider.id;
              const ConfigSection = provider.ConfigSection;

              return (
                <div
                  key={provider.id}
                  className={cn(
                    'cursor-pointer rounded-lg border p-4 transition-colors duration-150',
                    isSelected
                      ? 'border-white bg-seeker-800'
                      : 'border-seeker-800 hover:border-seeker-700',
                  )}
                  onClick={() => handleProviderChange(provider.id)}
                >
                  <div className="flex items-start gap-3">
                    {/* Only non-interactive copy lives inside the label: the native
                        radio's name is the provider name, described by its blurb. */}
                    <label className="flex min-w-0 flex-1 cursor-pointer items-start gap-3">
                      <input
                        type="radio"
                        name="uploadProvider"
                        value={provider.id}
                        checked={isSelected}
                        aria-labelledby={`${provider.id}-provider-name`}
                        aria-describedby={`${provider.id}-provider-description`}
                        onChange={() => handleProviderChange(provider.id)}
                        className="peer sr-only"
                      />
                      <span
                        aria-hidden="true"
                        className={cn(
                          'mt-0.5 flex size-4 shrink-0 items-center justify-center rounded-full border transition-colors duration-150 peer-focus-visible:ring-2 peer-focus-visible:ring-white/70',
                          isSelected ? 'border-white' : 'border-seeker-700',
                        )}
                      >
                        <span
                          className={cn(
                            'size-2 rounded-full bg-white transition-transform duration-150 ease-out',
                            isSelected ? 'scale-100' : 'scale-0',
                          )}
                        />
                      </span>
                      <span className="flex min-w-0 flex-col gap-1">
                        <span className="flex flex-wrap items-center gap-x-3 gap-y-2">
                          <img
                            src={provider.logo}
                            alt=""
                            aria-hidden="true"
                            className="h-6 w-auto object-contain"
                          />
                          <span
                            id={`${provider.id}-provider-name`}
                            className="text-sm font-bold leading-5 text-white"
                          >
                            {provider.info.name}
                          </span>
                        </span>
                        <span
                          id={`${provider.id}-provider-description`}
                          className="text-sm text-seeker-400"
                        >
                          {provider.info.description}
                        </span>
                      </span>
                    </label>
                    {provider.id === 's3' && (
                      <div className="flex shrink-0 flex-wrap items-center justify-end gap-2">
                        <InfoPill
                          tone="waiting"
                          icon={<KeyRound />}
                          label="BYOK"
                          title="Bring Your Own Keys"
                          points={[
                            'Use your own service credentials',
                            'Direct uploads to your account',
                            'Full control over your data',
                            'No third-party storage costs',
                          ]}
                        />
                        <InfoPill
                          tone="published"
                          icon={<Lock />}
                          label="Locally encrypted"
                          title="Your credentials are secure"
                          points={[
                            'Encrypted using your wallet signature',
                            'Stored locally in your browser only',
                            'Never sent to our servers',
                            'Direct browser-to-service uploads',
                          ]}
                        />
                      </div>
                    )}
                  </div>

                  <div className="mt-1 flex flex-wrap items-center gap-2 pl-7 text-xs leading-4 text-seeker-400">
                    <DollarSign
                      className="size-4 shrink-0"
                      aria-hidden="true"
                    />
                    <span>{provider.info.cost}</span>
                    {provider.info.links?.map((link) => (
                      <span key={link.url} className="flex items-center gap-2">
                        <span className="text-seeker-700">•</span>
                        <a
                          href={link.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="rounded text-white underline-offset-2 transition-colors duration-150 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/70"
                          onClick={(e) => e.stopPropagation()}
                        >
                          {link.label}
                        </a>
                      </span>
                    ))}
                  </div>

                  <AnimatePresence mode="wait">
                    {isSelected && (
                      <motion.div
                        key={`${provider.id}-config`}
                        initial={{ height: 0, opacity: 0 }}
                        animate={{
                          height: 'auto',
                          opacity: 1,
                          transition: { duration: 0.18, ease: EASE_OUT },
                        }}
                        exit={{
                          height: 0,
                          opacity: 0,
                          transition: { duration: 0.12, ease: EASE_OUT },
                        }}
                        className="overflow-hidden"
                      >
                        <ConfigSection
                          isSelected={isSelected}
                          onConfigUpdate={() => {
                            setProviderConfigs({
                              s3: hasS3Config(),
                            });
                          }}
                        />
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              );
            })}
          </div>
        </SettingsSection>
      </SettingsPage>
    </TooltipProvider>
  );
};
